<template>
    <div class="settings" :class="pageClass">
        <div class="settings__container">
            <nav class="settings__menu">
                <ul class="settings__menu-list">
                    <li v-for="menuSection in menu" :key="menuSection.name" class="settings__menu-item">
                        <h6 class="settings__menu-heading">
                            {{ menuSection.name }}
                        </h6>

                        <ul class="settings__menu-list">
                            <li v-for="menuItem in menuSection.children" :key="menuItem.name" class="settings__menu-item">
                                <a
                                    class="settings__menu-link"
                                    :class="getStateClass(menuItem)"
                                    @click="loadPage(menuItem.component, menuItem.path)"
                                >
                                    {{ menuItem.name }}
                                </a>
                            </li>
                        </ul>
                    </li>
                </ul>
            </nav>

            <component :is="selectedComponent" @input="onInput" />
        </div>

        <footer class="settings__footer">
            <div class="settings__footer-left">
                <button class="settings__button" type="button" @click="undo">
                    <font-awesome-icon :icon="['fal', 'undo']" />
                </button>

                <button class="settings__button" type="button" @click="redo">
                    <font-awesome-icon :icon="['fal', 'redo']" />
                </button>
            </div>

            <div class="settings__footer-right">
                <button class="settings__button" type="button" @click="save">
                    <font-awesome-icon :icon="['fal', 'save']" />
                </button>
            </div>
        </footer>

        <snackbar />

        <loading-page :visible-default="false" />
    </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';

import LoadingPage from '@/components/shared/loading/loading-page';
import { STATE_CLASSES } from '@/data/constants/classes';
import menu from '@/data/menu/settings.json';

import {
    LOAD_SETTINGS,
    SAVE_SETTINGS,
    LOAD_PRESETS,
} from '@/store/types/action-types';

import {
    SET_SETTINGS_DATA,
    TOGGLE_PAGE_LOADING,
    SHOW_ALERT,
    SET_CHANGED_DATA,
} from '@/store/types/mutation-types';

export default {
    name: 'settings',

    components: {
        LoadingPage,
        SettingsAnimations: () => import('@/components/settings/component/settings-animations'),
        SettingsBrandColors: () => import('@/components/settings/color/settings-brand-colors'),
        SettingsUtilityColors: () => import('@/components/settings/color/settings-utility-colors'),
        SettingsGradients: () => import('@/components/settings/color/settings-gradients'),
        SettingsHtmlDefaults: () => import('@/components/settings/typography/settings-html-defaults'),
        SettingsFonts: () => import('@/components/settings/typography/settings-fonts'),
        SettingsPresetsHeading: () => import('@/components/settings/preset/settings-presets-heading'),
        SettingsPresetsParagraph: () => import('@/components/settings/preset/settings-presets-paragraph'),
        SettingsPresetsButton: () => import('@/components/settings/preset/settings-presets-button'),
        SettingsPresetsLink: () => import('@/components/settings/preset/settings-presets-link'),
        SettingsSpacing: () => import('@/components/settings/component/settings-spacing'),
        SettingsGoogle: () => import('@/components/settings/system/settings-google'),
        SettingsFlex: () => import('@/components/settings/system/settings-flex'),
        SettingsAccessibility: () => import('@/components/settings/system/settings-accessibility'),
    },

    data() {
        return {
            menu,
            selectedComponent: 'settings-brand-colors',
        };
    },

    computed: {
        ...mapState({
            settings: state => state.settings,
        }),

        pageClass() {
            return this.selectedComponent === 'settings-animations' ? 'settings--animations' : '';
        },
    },

    created() {
        this.loadSettings();
        this.loadInitialPage();
    },

    methods: {
        ...mapActions({
            loadSettings: LOAD_SETTINGS,
            saveSettings: SAVE_SETTINGS,
            loadPresets: LOAD_PRESETS,
        }),

        ...mapMutations({
            setChangedData: SET_CHANGED_DATA,
            setSettingsData: SET_SETTINGS_DATA,
            showAlert: SHOW_ALERT,
            togglePageLoading: TOGGLE_PAGE_LOADING,
        }),

        loadInitialPage() {
            // use the first component in the menu as the default
            let { component, path } = this.menu[0].children[0];
            const routePath = this.$route.params.path;

            // check if a route is set to load a specific component
            if (routePath) {
                const matchingMenuItem = this.menu
                    .map(section => section.children)
                    .flat()
                    .find(menuItem => menuItem.path === routePath);

                if (matchingMenuItem) {
                    component = matchingMenuItem.component;
                    path = matchingMenuItem.path;
                }
            }

            this.loadPage(component, path);
        },

        loadPage(component, path) {
            this.selectedComponent = component;
            this.$router.push({ path: `/settings/${ path }` }).catch(() => {});
            window.scrollTo(0, 0);
        },

        getStateClass(menuItem) {
            return {
                [STATE_CLASSES.SELECTED]: menuItem.component === this.selectedComponent,
            };
        },

        onInput(prop, data) {
            this.setSettingsData({
                ...this.settings,
                [prop]: data,
            });

            this.setChangedData(true);
        },

        async save() {
            this.togglePageLoading({
                loading: true,
                message: 'Saving your settings',
            });

            await this.saveSettings(this.settings);
            this.showAlert({ description: 'Settings rebuild triggered. Please wait up to 10 minutes to see your changes reflected.' });
            await this.loadPresets();

            this.setChangedData(false);
            this.togglePageLoading({ loading: false });
        },

        undo() {
            // TBD
        },

        redo() {
            // TBD
        },
    },
};

</script>

<style lang="scss">
.settings {
    $this: &;
    color: var(--setting-color);
    background-color: var(--setting-backgroundColor);

    &--animations {
        padding-bottom: 5rem;
    }

    &__container {
        #{ $this }--animations & {
            height: 100%;
        }
    }

    &__menu {
        position: fixed;
        padding: 85px 50px;
        top: 0;
        left: 0;
        height: 100vh;
        overflow: auto;
    }

    &__menu-heading {
        color: var(--setting-color);
        font-weight: 600;
        margin-bottom: 8px;
    }

    &__menu-list {
        list-style: none;
        margin-bottom: 32px;
    }

    &__menu-link {
        display: block;
        text-decoration: none;
        color: var(--setting-linkColor);
        font-size: 15px;
        padding: 4px 16px;

        &:hover {
            cursor: pointer;
            color: var(--setting-linkHoverColor);
        }

        &.#{ $is-selected } {
            font-weight: 700;
        }
    }

    &__content {
        padding: 3.125rem 3.125rem 8.75rem 18.75rem;

        #{ $this }--animations & {
            display: flex;
            flex-direction: column;
            height: calc(100% - #{ $settings-footer-height });
            padding-bottom: 2rem;
        }
    }

    &__header {
        margin-bottom: 90px;
    }

    &__title {
        font-size: 24px;
    }

    &__description {
        margin-top: 15px;
        opacity: 0.9;
    }

    &__footer {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 100;
        color: var(--setting-footerColor);
        background-color: var(--setting-footerBackgroundColor);
        display: flex;
        justify-content: space-between;
        box-shadow: 0 -2px 10px -5px #2c3e50;
        height: $settings-footer-height;
    }

    &__footer-left {
        display: flex;
    }

    &__footer-right {
        display: flex;
    }

    &__button {
        width: 45px;
        height: 40px;
        color: var(--form-buttonColor);
        background-color: var(--form-buttonBackgroundColor);
        display: flex;
        justify-content: center;
        align-items: center;
        transition: all 0.45s;

        &:hover {
            color: var(--form-buttonHoverColor);
            background-color: var(--form-buttonHoverBackgroundColor);
        }

        &:active {
            transform: scale(0.9);
        }

        svg {
            position: relative;
            top: 1px;
        }
    }
}

.text-setting {
    $this: &;

    &__section {
        display: flex;

        &:not(:last-of-type) {
            margin-bottom: 70px;
        }
    }

    &__content {
        min-width: 300px;
    }

    &__header {
        display: flex;
        justify-content: space-between;
    }

    &__title {
        display: inline-block;
        font-weight: 700;
        margin-bottom: 20px;

        &--editable {
            padding: 2px 4px;
            border: 1px solid transparent;
            border-bottom-color: var(--form-borderColor);
            transition: border-color 0.4s ease;

            &:focus {
                border-color: var(--form-borderColor);
            }
        }
    }

    &__options {
        margin-bottom: 15px;
    }

    &__option {
        font-size: 15px;
        padding: 4px 5px 6px;
        border-radius: 6px;
        width: 36px;
        height: 30px;
        border: 1px solid transparent;
        opacity: 0.9;

        &:hover {
            color: var(--app-color);
        }

        &:not(:first-of-type) {
            margin-left: 5px;
        }

        &.#{ $is-selected } {
            color: var(--app-color);
            border-color: var(--app-color);
            opacity: 1;
        }

        &--hover {
            position: relative;
            top: -1px;
            margin-left: 15px;
            width: auto;
            height: auto;
            padding: 6px 7px 4px;
            font-size: 13px;
            font-weight: 600;

            &.#{ $is-selected } {
                color: $attention-color;
                border-color: $attention-color;
            }
        }
    }

    &__list {
        flex-basis: 250px;
    }

    &__item {
        &:not(:first-of-type) {
            margin-top: 10px;
        }
    }

    &__preview {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__preview-container {
        position: relative;
        color: black;
        background-color: white;
        border-bottom-color: var(--form-borderColor);
        display: inline-block;
        vertical-align: middle;
        text-align: center;
        border: 1px solid darken(white, 10%);
        transition: border-color 0.4s ease;
        min-width: 300px;
        max-width: 820px;
        padding: 3em;
        transition: all 0.25s;

        &:focus-within {
            border-color: var(--form-borderColor);
        }

        &:before {
            content: "Preview";
            position: absolute;
            bottom: -2em;
            left: 50%;
            transform: translateX(-50%);
            color: darken(white, 30%);
            font-size: 0.8125rem;
            font-weight: 600;
        }

        &.#{ $is-paragraph } {
            padding: 1.25em;

            &:focus-within {
                border-bottom-color: var(--form-borderColor);
            }
        }

        &.#{ $is-button } {
            border: none;
        }
    }

    &__button {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        position: relative;
        width: 100px;
        height: 100px;
        margin: 100px 0 80px;
        border: dashed 2px var(--app-color);
        color: var(--app-color);
        transition: all 0.33s;
        opacity: 0.5;

        &:hover {
            opacity: 1;
            cursor: pointer;
        }
    }

    &__button-icon {
        font-size: 48px;
        margin-top: 4px;
        opacity: 0.9;
    }

    &__button-text {
        font-size: 16px;
        position: absolute;
        bottom: -40px;
        left: 0;
        right: 0;
        font-weight: 700;
        white-space: nowrap;
        margin-left: -100%;
        margin-right: -100%;
        text-align: center;
    }

    .vc-chrome {
        font-family: $default-font-family;
        position: absolute;
        z-index: 100;
        top: 0;
        left: 100%;

        .vc-chrome-toggle-icon svg {
            margin-left: 12px;
        }
    }
}
</style>
